<script setup lang="ts">
    import { Form } from 'vee-validate'
    import * as yup from 'yup'
    import { useRoute } from 'vue-router';
    const route = useRoute();
    const router = useRouter();
    console.log('route name: ',route.name)
    const props = defineProps({
        type: {
            type: String as () => 'modal' | 'none',
            default: 'none',
            required: false,
        }
    })

    const { signIn } = useAuth()

    const { validate, resetForm } = useForm({});
    const form = ref(null);
    const formData = ref({
        email: '',
        password: '',
    });

    const schema = yup.object().shape({
        email: yup.string().email('A valid email is required').required('Email is required'),
        password: yup.string().required('Password is required'),
    })

    const onInvalidSubmit = ({ errors }: any) => {
        const firstErrorFieldName = Object.keys(errors)[0];
        scrollToValidation(firstErrorFieldName);
    }

    const submit = async(values: any) => {
        const validator = await validate();

        if (!validator.valid) {
            return;
        }

        const payload = {
            ...values,
            type: 'user'
        };

        try {
            const result = await signIn('credentials', {
                redirect: false,
                callbackUrl: '/',
                ...payload
            });
             const { error } = result as { error?: string | null };
            if(error){
                showNotificationError(`The provided credentials are incorrect.`);
                return;
            }
            if(route.name === 'login'){
                router.push('/');
            }
        } catch (error) {
            showNotificationError(`Error: ${error}`);
            console.error('Caught error in signIn:', error);
        }
    }

    const signInFacebook = async() => {
        await signIn('facebook')
    }

    const signInGoogle = async() => {
        await signIn('google')
    }

    const scrollToValidation = (errorName: any) => {
        const el = document.querySelector(`[name="${errorName}"]`);
        if (el) {
            el.scrollIntoView({ block: 'start', behavior: 'smooth' });
        }
    };

    // For parent processing
    const emit = defineEmits<{
        (e: 'closeEvent'): void,
        (e: 'successEvent'): void
    }>()

    const handleExit = () => {
        emit('closeEvent') // Emit the event when the button is clicked
    }

    const classObject = reactive({
        'w-[42.4rem] px-[4rem] py-[2.4rem] bg-white mx-auto rounded-[1rem] max-md:w-[calc(100%-3.2rem)] max-md:my-[2rem] max-md:px-[2rem]' : props.type == 'none',
        'm-auto w-full px-[3.6rem] py-[2rem]' : props.type == 'modal',
    })
    
</script>

<template>
    <Form 
        ref="form"
        @submit="submit"
        @invalid-submit="onInvalidSubmit"
        :initial-values="formData"
        :validation-schema="schema"
        :class="[
            classObject,
        ]"
    >
        <TitleText
            class="text-center"
            type="4"
        >
        Sign In
        </TitleText>
        <Block class="mt-[2.4rem]">
            <Block>
                <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block" for="">Your email</label>
                <InputText
                    class="placeholder-[secondary] font-normal text-[1.4rem] py-[1.2rem] px-[1.6rem] w-full border-[lightGray] border-solid border-[0.1rem] rounded-[1rem]"
                    inputName="email"
                    icon="none"
                    inputType="email"
                    placeholder=""
                />
            </Block>
            <Block class="mt-[1.2rem]">
                <label class="font-bold text-[1.4rem] leading-[1.5] mb-[.4rem] block" for="">Password</label>
                <InputPassword
                    class="placeholder-[secondary] font-normal text-[1.4rem] py-[1.2rem] px-[1.6rem] w-full border-[lightGray] border-solid border-[0.1rem] rounded-[1rem]"
                    inputName="password"
                    icon="none"
                    placeholder=""
                />
            </Block>

            <Block class="mt-[1.2rem] flex items-center gap-[.8rem] rounded-[2rem]">
                <label class="flex items-center">
                    <div class="relative">
                        <input type="checkbox"
                            class="opacity-0 absolute checked:opacity-[1] checked:top-2/4 checked:-translate-y-2/4 checked:-translate-x-2/4 checked:left-2/4 checked:w-6 checked:h-6">
                        <div
                            class="flex items-center justify-center w-6 h-6 bg-white border border-gray-300 border-solid rounded-md">
                        </div>
                    </div>
                </label>

                <span class="text-checkbox text-[1.4rem] leading-[1.5] font-normal text-[secondary] cursor-pointer">
                    Remember this device
                </span>
            </Block>

            <Block class="text-center mt-[1.2rem]">
                <NuxtLink 
                    to="/password/forgot" 
                    class="inline-block text-center text-[1.4rem] font-normal leading-[1.5] text-[blueLink]">Forgot your Password？
                </NuxtLink>
            </Block>

            <button id="submit-btn"
                class="mt-[1.2rem] bg-[goldenYellow] text-[primary] font-bold text-[1.4rem] leading-[1.5] rounded-[1rem] p-[1rem] w-full">
                Sign In
            </button>
            
            <Block class="mt-[2.4rem]">
                <a-divider style="height: 1px; background-color: gray; opacity: 50%;" />
                <p class="text-[secondary] text-[1.4rem] leading-[1.5] text-center">
                    Sign in with
                </p>



                <Block class="mt-[1.2rem] flex flex-wrap gap-[.8rem]">
                        
                        <Block class="flex flex-grow justify-between gap-[0.8rem]">
                            <button 
                                type="button" 
                                @click="signInGoogle()" 
                                class="border-solid border-[.1rem] border-[lightGray] h-[3.7rem] w-full block text-center rounded-[.5rem]">
                                <span class="google-btn-span">Google</span>
                            </button>
                            <button 
                                type="button" 
                                @click="signInFacebook()" 
                                class="fb-btn w-full text-center h-[3.7rem] rounded-[.5rem] border-solid border-[.1rem] border-[lightGray]">
                                <span class="fb-btn-span">Facebook</span>
                            </button>
                            <!-- <button 
                                type="button" 
                                class="x-btn w-full text-center h-[3.7rem] rounded-[.5rem] border-solid border-[.1rem] border-[lightGray]">
                                <span class="x-btn-span">X</span>
                            </button> -->
                        </Block>
                </Block>
            </Block>

            <Block class="mt-[2.4rem] text-center">
                <span class="text-[1.4rem] leading-[1.5]">Don’t have an account?</span>
                <NuxtLink to="/register" class="underline text-[blueLink] font-normal text-[1.4rem] inline-block ml-[1.6rem]">Register</NuxtLink>
            </Block>
        </Block>
    </Form>
</template>